// ABOUTME: Column settings popover for ShDataGrid3 — row height, per-column visibility and pinning.
// ABOUTME: Also hosts the trigger for the row-grouping popover (GridGrouping3).

import { useMemo, useState } from "react";
import { Box, Button, IconButton, List, useTheme } from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AlignHorizontalLeftIcon from "@mui/icons-material/AlignHorizontalLeft";
import AlignHorizontalRightIcon from "@mui/icons-material/AlignHorizontalRight";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { tokens } from "../../../theme";
import { ReactComponent as PinAlt } from "../../../styles/svg/pin.svg";
import PopoverModal from "../Modal/PopoverModal";
import Subheading from "../text/Subheading";
import Text from "../text/Text";
import Shbutton from "../buttons/Shbutton";
import ShSwitch from "../inputs/ShSwitch";
import { DropdownBase } from "../dropdowns/Dropdown";
import GridGrouping3 from "./GridGrouping3";
import { resolveDateGranularities } from "./groupingDateGranularities";

import { useGridFilter } from "../../../contexts/GridFilterContext";
import {
    setShGridColumns,
    updateRowHeight,
    updateGroupBy,
} from "../../../newapi/global/filter/gridFilterSlice";

import {
    shDatagridRowHeightOptions,
    SH_DATAGRID_DEFAULT_ROW_HEIGHT,
} from "../../../newapi/global/filter/DataGridGeneralOptions";

/**
 * Return a new runtime columns array with `field` set to `nextPinned`. Storage order is
 * deliberately NOT rearranged — pin changes only flip a flag, and the render-side zone
 * sort in ShDataGrid3 is what visually relocates the column to the correct edge. Keeping
 * storage order stable means unpinning a column returns it to its original natural
 * position (the position it had before any pin change), which is what users expect.
 * Drag-to-reorder is the only thing that mutates stored order.
 */
const setPinned = (columns, field, nextPinned) => {
    if (!Array.isArray(columns)) return columns;
    return columns.map((c) =>
        c.column === field ? { ...c, pinned: nextPinned } : c
    );
};

const GridcolumnSettings3View = ({ columns, onSaveAsNew, onUpdateView, hasActiveConfig, disableViewSaving }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const dispatch = useDispatch();
    const { index, id } = useGridFilter();

    const runtimeColumns = useSelector((state) => {
        return state.gridFilter?.viewInstances?.[index]?.[id]?.columns || [];
    });

    const runtimeGroupBy = useSelector((state) => {
        return state.gridFilter?.viewInstances?.[index]?.[id]?.groupBy ?? null;
    });

    // Grouping popover anchored to the trigger row below.
    const [groupingAnchorEl, setGroupingAnchorEl] = useState(null);
    const groupingOpen = Boolean(groupingAnchorEl);
    const handleGroupingClick = (event) => setGroupingAnchorEl(event.currentTarget);
    const handleGroupingClose = () => setGroupingAnchorEl(null);

    const hasGroupableColumns = Array.isArray(columns)
        && columns.some((c) => c?.grouping);

    // Currently active grouping column — the trigger shows its `grouping.preview`
    // JSX directly (e.g. the styled ShLabel for state) rather than its header
    // name. Falls back to a "None" text when no group is active.
    const activeGroupColumn = runtimeGroupBy?.field && Array.isArray(columns)
        ? columns.find((c) => c.field === runtimeGroupBy.field)
        : null;

    // Granularity metadata for the inline dropdown in the trigger. Memoized so
    // the granularity list + current-index resolution only recomputes when the
    // active column or runtime groupBy changes, not on every render.
    const activeGranularityInfo = useMemo(() => {
        const grans =
            Array.isArray(activeGroupColumn?.grouping?.granularities) &&
            activeGroupColumn.grouping.granularities.length > 0
                ? resolveDateGranularities(activeGroupColumn.grouping.granularities)
                : [];
        if (grans.length === 0) return { grans, currentId: null, currentIdx: -1, currentGran: null };
        const currentId = runtimeGroupBy?.granularity ?? grans[0].id;
        const currentIdx = Math.max(0, grans.findIndex((g) => g.id === currentId));
        return { grans, currentId, currentIdx, currentGran: grans[currentIdx] };
    }, [activeGroupColumn, runtimeGroupBy]);

    // Row height is per-config — read the runtime value from gridFilterSlice.
    // It gets persisted into the config when the user explicitly saves.
    const currentRowHeight = useSelector(
        (state) =>
            state.gridFilter?.settings?.rowHeight
            ?? SH_DATAGRID_DEFAULT_ROW_HEIGHT
    );

    // Merge runtime columns with any code columns absent from runtime (happens on
    // locally-owned saved configs where new code columns aren't auto-appended).
    // Runtime entries come first to preserve the user's saved order. phoneView is
    // the mobile card renderer, not a real column, so it's excluded from settings.
    const displayColumns = useMemo(() => {
        const codeByField = new Map();
        if (Array.isArray(columns)) {
            for (const c of columns) {
                if (c.field === "phoneView") continue;
                codeByField.set(c.field, c);
            }
        }
        const seen = new Set();
        const merged = [];
        for (const rc of runtimeColumns) {
            if (rc.column === "phoneView") continue;
            seen.add(rc.column);
            const code = codeByField.get(rc.column);
            merged.push({
                ...rc,
                name: rc.name || code?.headerName || rc.column,
            });
        }
        for (const code of codeByField.values()) {
            if (seen.has(code.field)) continue;
            merged.push({
                column: code.field,
                name: code.headerName || code.field,
                visible: false,
                pinned: null,
            });
        }
        return merged;
    }, [runtimeColumns, columns]);

    const toggleVisibility = (column) => {
        const exists = runtimeColumns.some((c) => c.column === column.column);
        if (!exists) {
            // Adding a code-only column: show it by flipping from implicit-hidden to visible.
            const next = [
                ...runtimeColumns,
                { column: column.column, visible: true, pinned: null },
            ];
            dispatch(setShGridColumns({ index, id, columns: next }));
            return;
        }
        const next = runtimeColumns.map((c) =>
            c.column === column.column ? { ...c, visible: !c.visible } : c
        );
        dispatch(setShGridColumns({ index, id, columns: next }));
    };

    const togglePin = (column, side) => {
        const exists = runtimeColumns.some((c) => c.column === column.column);
        if (!exists) {
            // Pinning a code-only column promotes it into runtime; pin implies visible.
            const next = [
                ...runtimeColumns,
                { column: column.column, visible: true, pinned: side },
            ];
            dispatch(setShGridColumns({ index, id, columns: next }));
            return;
        }
        const alreadyOnThisSide = column.pinned === side;
        const nextPinned = alreadyOnThisSide ? null : side;
        const next = setPinned(runtimeColumns, column.column, nextPinned);
        dispatch(setShGridColumns({ index, id, columns: next }));
    };

    const selectRowHeight = (value) => {
        // Row height is per-config — only update the runtime mirror. It gets
        // persisted into the config payload when the user explicitly saves.
        dispatch(updateRowHeight(value));
    };

    return (
        <Box
            sx={{
                width: "520px",
                maxHeight: "500px",
                display: "flex",
                flexDirection: "column",
                "@media screen and (max-width: 47.9375em)": {
                    width: 1,
                },
            }}
        >
            {/* Scrollable content area — everything except the save footer */}
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                {/* Group-by trigger row — opens the GridGrouping3 popover on top.
                    Only rendered when at least one code column declared a `grouping`
                    block at the call site. */}
                {hasGroupableColumns && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            paddingTop: 3,
                            paddingLeft: 3,
                            paddingRight: 3,
                            paddingBottom: 3,
                            borderBottom: `1px solid ${colors.grey[100]}`,
                        }}
                    >
                        <Subheading>{t("group_by")}</Subheading>
                        <Box
                            onClick={handleGroupingClick}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                width: 1,
                                height: 40,
                                borderRadius: 5,
                                paddingX: 1.5,
                                cursor: "pointer",
                                userSelect: "none",
                                background:
                                    theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                boxShadow:
                                    theme.palette.mode === "dark"
                                        ? "none"
                                        : `0px 2px 4px ${colors.grey[200]}`,
                                border:
                                    theme.palette.mode === "dark"
                                        ? "none"
                                        : `1px solid ${colors.grey[100]}`,
                                    transition: "250ms",
                                "&:hover": {
                                    transition: "250ms",
                                    background:
                                        theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[100],
                                    boxShadow:
                                        theme.palette.mode === "dark"
                                            ? "none"
                                            : `0px 2px 4px ${colors.grey[200]}`,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    marginRight: 1.5,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: colors.txt.secondary,
                                    "& svg": { width: 20 },
                                    "& *": { fill: colors.txt.secondary },
                                }}
                            >
                                <WorkspacesOutlinedIcon fontSize="small" />
                            </Box>
                            <Text semibold sx={{ color: colors.txt.primary }}>
                                {activeGroupColumn
                                    ? t(activeGroupColumn.headerName || activeGroupColumn.field)
                                    : t("none")}
                            </Text>
                            <Box
                                sx={{
                                    marginLeft: "auto",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    color: colors.txt.secondary,
                                }}
                            >
                                {activeGranularityInfo.grans.length > 0 ? (
                                    // Inline granularity dropdown — stopPropagation so
                                    // clicking the pill doesn't bubble up and open the
                                    // GridGrouping3 popover.
                                    <Box
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                    >
                                        <DropdownBase
                                            key={activeGranularityInfo.currentId}
                                            options={activeGranularityInfo.grans.map((g) => t(g.label))}
                                            onChange={(selectedIdx) => {
                                                const chosen = activeGranularityInfo.grans[selectedIdx];
                                                if (!chosen || !activeGroupColumn) return;
                                                dispatch(
                                                    updateGroupBy({
                                                        index,
                                                        id,
                                                        groupBy: {
                                                            field: activeGroupColumn.field,
                                                            granularity: chosen.id,
                                                        },
                                                    })
                                                );
                                            }}
                                            selected={activeGranularityInfo.currentIdx}
                                            noSelection={t("no_label")}
                                            closeOnSelection
                                            disallowDeselection
                                            transformOrigin={{ vertical: "top" }}
                                            anchorOrigin={{ vertical: "bottom" }}
                                        >
                                            <Box
                                                className="active"
                                                sx={{
                                                    borderRadius:4,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    height: 32,
                                                    px: 1.5,
                                                    cursor: "pointer",
                                                    userSelect: "none",
                                                    background:
                                                        theme.palette.mode === "dark"
                                                            ? colors.grey[200]
                                                            : colors.grey[100],
                                                    "&:hover": {
                                                        transition: "250ms",
                                                        background:
                                                            theme.palette.mode === "dark"
                                                                ? colors.grey[300]
                                                                : colors.bg.tertiary,
                                                    },
                                                }}
                                            >
                                                <Text
                                                    medium
                                                    sx={{ fontSize: 12, color: colors.txt.primary }}
                                                >
                                                    {activeGranularityInfo.currentGran ? t(activeGranularityInfo.currentGran.label) : null}
                                                </Text>
                                            </Box>
                                        </DropdownBase>
                                    </Box>
                                ) : (
                                    activeGroupColumn?.grouping?.preview
                                )}
                                <ChevronRightIcon fontSize="small" />
                            </Box>
                        </Box>
                    </Box>
                )}

                <GridGrouping3
                    open={groupingOpen}
                    anchorEl={groupingAnchorEl}
                    onClose={handleGroupingClose}
                    columns={columns}
                />

                {/* Row height picker — segmented pills. One button per preset from
                    shDatagridRowHeightOptions plus the "auto" sentinel which flips the grid
                    into content-fit mode with an 8px top/bottom padding minimum. */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        paddingTop: 3,
                        paddingLeft: 3,
                        paddingRight: 3,
                        paddingBottom: 3,
                        borderBottom: `1px solid ${colors.grey[100]}`,
                    }}
                >
                    <Subheading>{t("row_height")}</Subheading>
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                        }}
                    >
                        {shDatagridRowHeightOptions.map((option) => {
                            const isActive = currentRowHeight === option.value;
                            return (
                                <Button
                                    key={String(option.value)}
                                    onClick={() => selectRowHeight(option.value)}
                                    disableElevation
                                    sx={{
                                        textTransform: "none",
                                        fontFamily: "Roboto, sans-serif",
                                        fontSize: 14,
                                        fontWeight: isActive ? 700 : 400,
                                        height: 32,
                                        minWidth: 0,
                                        px: 2,
                                        borderRadius: 4,
                                        color: isActive
                                            ? theme.palette.mode === "dark"
                                                ? colors.blue[400]
                                                : colors.primary[400]
                                            : colors.txt.secondary,
                                        background: isActive
                                            ? theme.palette.mode === "dark"
                                                ? colors.blue[100]
                                                : colors.primary[100]
                                            : theme.palette.mode === "dark"
                                                ? colors.grey[400]
                                                : colors.grey[100],
                                        "&:hover": {
                                            background: isActive
                                                ? theme.palette.mode === "dark"
                                                    ? colors.blue[100]
                                                    : colors.primary[100]
                                                : theme.palette.mode === "dark"
                                                    ? colors.grey[300]
                                                    : colors.grey[200],
                                        },
                                    }}
                                >
                                    {t(option.i18nKey)}
                                </Button>
                            );
                        })}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingTop: 3,
                        paddingLeft: 3,
                        paddingRight: 1.75,
                    }}
                >
                    <Subheading>{t("columns")}</Subheading>
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            "& .pin-icon path": {
                                fill: colors.txt.primary,
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: 48,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <RemoveRedEyeOutlinedIcon />
                        </Box>
                        <Box
                            sx={{
                                ml:1,
                                width: 74,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <PinAlt className="pin-icon" />
                        </Box>
                    </Box>
                </Box>

                <Box>
                    <List
                        sx={{
                            padding: 0,
                            paddingBottom: 2.5,
                        }}
                    >
                        {displayColumns.map((column) => {
                            const pinnedLeft = column.pinned === "left";
                            const pinnedRight = column.pinned === "right";
                            return (
                                <Box
                                    key={column.column}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        paddingTop: 0.5,
                                        paddingLeft: 3,
                                        paddingRight: 1.25,
                                    }}
                                >
                                    <Text light>{t(column.name || column.column)}</Text>
                                    <Box
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 2,
                                            "& .pin-icon path": {
                                                fill: colors.txt.secondary,
                                            },
                                            "& .pin-icon--active path": {
                                                fill:
                                                    theme.palette.mode === "dark"
                                                        ? colors.blue[400]
                                                        : colors.primary[400],
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 48,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <ShSwitch
                                                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                                onChange={() => toggleVisibility(column)}
                                                checked={column.visible !== false}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                height: 32,
                                                borderRadius: 4,
                                                px:0.5,
                                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                                boxShadow: theme.palette.mode === "dark" ? "none" : `0px 2px 4px ${colors.grey[200]}`,
                                                border: theme.palette.mode === "dark" ? `1px solid ${colors.grey[500]}` : `1px solid ${colors.grey[100]}`,
                                                overflow: "hidden",
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() => togglePin(column, "left")}
                                                aria-label={t("pin_left")}
                                                sx={{
                                                    width: 32,
                                                    height: 24,
                                                    borderRadius: 3,
                                                    background: pinnedLeft
                                                        ? (theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100])
                                                        : "transparent",
                                                }}
                                            >
                                                <AlignHorizontalLeftIcon
                                                    sx={{
                                                        fontSize: 16,
                                                        color: pinnedLeft
                                                            ? (theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400])
                                                            : colors.txt.secondary,
                                                    }}
                                                />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => togglePin(column, "right")}
                                                aria-label={t("pin_right")}
                                                sx={{
                                                    width: 32,
                                                    height: 24,
                                                    borderRadius: 3,
                                                    background: pinnedRight
                                                        ? (theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100])
                                                        : "transparent",
                                                }}
                                            >
                                                <AlignHorizontalRightIcon
                                                    sx={{
                                                        fontSize: 16,
                                                        color: pinnedRight
                                                            ? (theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400])
                                                            : colors.txt.secondary,
                                                    }}
                                                />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </List>
                </Box>
            </Box>

            {/* Static footer — save buttons, don't scroll with the list above */}
            {(!disableViewSaving && (onSaveAsNew || onUpdateView)) && (
                <Box
                    sx={{
                        background:theme.palette.mode === "dark" ?  colors.grey[400]: colors.grey[100],
                        flexShrink: 0,
                        paddingX: 3,
                        paddingY: 2,
                        display: "flex",
                        // flexDirection:'column',
                        gap: 1,
                    }}
                >
                    {onSaveAsNew && (
                        <Shbutton
                            onClick={onSaveAsNew}
                            color={theme.palette.mode === "dark" ? "primaryGrey" : "grey"}
                            endIcon={<SaveOutlinedIcon fontSize="small" />}
                            sx={{ width: 1 }}
                            variant="contained"
                            isSidepanel
                            size="32"
                        >
                            {t("save_as_view")}
                        </Shbutton>
                    )}
                    {hasActiveConfig && onUpdateView && (
                        <Shbutton
                            onClick={onUpdateView}
                            color={theme.palette.mode === "dark" ? "blue" : "primary"}
                            variant="contained"
                            sx={{ width: 1 }}
                            size="32"
                        >
                            {t("save")}
                        </Shbutton>
                    )}
                </Box>
            )}
        </Box>
    );
};

const GridcolumnSettings3 = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { columns, onSaveAsNew, onUpdateView, hasActiveConfig, ...popoverProps } = props;

    return (
        <PopoverModal
            {...popoverProps}
            triangle={{
                position: "center",
                color: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
            }}
        >
            <Box
                sx={{
                    background:
                        theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    width: 520,
                    "@media screen and (max-width: 768px)": {
                        width: 1,
                    },
                }}
            >
                <GridcolumnSettings3View
                    columns={columns}
                    onSaveAsNew={onSaveAsNew}
                    onUpdateView={onUpdateView}
                    hasActiveConfig={hasActiveConfig}
                    disableViewSaving={props.disableViewSaving}
                />
            </Box>
        </PopoverModal>
    );
};

export default GridcolumnSettings3;
export { setPinned };
